// src/store/auth/authSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';


import config from '../../config';

const baseUrl = config.baseUrl;
const appsEndpoint = '/api/some-protected-route';
const authEndpoint = '/api/auth/login';
const logoutEndpoint = '/api/auth/logout';

// Lee user persistido
const saved = localStorage.getItem('user');
const initialUser = saved ? JSON.parse(saved) : null;

const initialState = {
  loading: false,
  isAuthenticated: initialUser !== null,
  user: initialUser,
  applications: [],
  error: null
};

// Thunk para login
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const userAgent = navigator.userAgent;
      const response = await axios.post(
        `${baseUrl}${authEndpoint}`,
        { login: username, password, user_agent: userAgent },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const token = response.data.token;
      const email = response.data.email;
      const name = response.data.name;

      // fetch apps
      const appsResponse = await axios.get(
        `${baseUrl}${appsEndpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const applications = appsResponse.data.applications || [];

      // persiste
      const userToSave = { token, username, email, name };
      localStorage.setItem('user', JSON.stringify(userToSave));

      return { token, email, name, applications };
    } catch (error) {
      console.error('Error during login process:', error);
      const message = error.response?.data?.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

// Thunk para logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const savedUser = localStorage.getItem('user');
      const { token } = savedUser ? JSON.parse(savedUser) : { token: '' };
      if (token) {
        await axios.post(
          `${baseUrl}${logoutEndpoint}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      localStorage.removeItem('user');
      dispatch(clearLogout());
    } catch (error) {
      console.error('Logout failed:', error);
      return rejectWithValue('Logout failed');
    }
  }
);

// Thunk para fetchApplications
export const fetchApplications = createAsyncThunk(
  'auth/fetchApplications',
  async (_, { rejectWithValue }) => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return rejectWithValue('No user logged');
    const { token } = JSON.parse(savedUser);
    try {
      const response = await axios.get(
        `${baseUrl}${appsEndpoint}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.applications || [];
    } catch (error) {
      console.error('Error fetching applications:', error);
      return rejectWithValue('Failed to fetch applications');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLogout(state) {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.applications = [];
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        const { token, email, name, applications } = action.payload;
        state.user = {
          token,
          username: action.meta.arg.username,
          email,
          name
        };
        state.applications = applications;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.applications = [];
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applications = action.payload;
      });
  }
});

export const { clearLogout } = authSlice.actions;
export default authSlice.reducer;
